import React, { useState, useEffect, useRef } from 'react';
import { CTAComponent } from '../../Common/UI/client/components/CTA';
import { CarouselContainer, FileReference, HomepageHeroV2Props } from './models';
import './styles.scss';

const HomepageHeroV2: React.FC<HomepageHeroV2Props> = ({ data }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [titleVisible, setTitleVisible] = useState(false);

  const progressIntervalRef = useRef<any>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  // Use refs to always have latest values inside intervals
  const currentSlideRef = useRef(0);
  const isPlayingRef = useRef(true);
  const progressRef = useRef(0);

  const carouselData = data as CarouselContainer;
  const slides = carouselData?.slides || [];
  const totalSlides = slides.length;
  const carouselItem = slides[currentSlide];
  const autoplaySpeed = carouselData ? (Number.parseInt(carouselData.delay) * 1000 || 8000) : 8000;
  const isAutoplay = carouselData?.autoplay === 'true';
  const ringCircumference = 2 * Math.PI * 21.5; // SVG ring, r=21.5 → 135.088

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current as any);
      progressIntervalRef.current = null;
    }
  };

  const startImageProgress = () => {
    clearProgressInterval();
    progressRef.current = 0;
    setProgress(0);

    progressIntervalRef.current = setInterval(() => {
      progressRef.current += (360 / (autoplaySpeed / 100));
      setProgress(progressRef.current);

      if (progressRef.current >= 360) {
        progressRef.current = 0;
        setProgress(0);
        if (totalSlides > 1 && isAutoplay && isPlayingRef.current) {
          setCurrentSlide(prev => {
            const next = prev < totalSlides - 1 ? prev + 1 : 0;
            currentSlideRef.current = next;
            return next;
          });
        }
      }
    }, 100);
  };

  const handleSlideChange = () => {
    clearProgressInterval();
    progressRef.current = 0;
    setProgress(0);
    if (isPlayingRef.current) {
      startImageProgress();
    }
  };

  const handlePrevious = () => {
    setCurrentSlide(prev => {
      const next = prev > 0 ? prev - 1 : totalSlides - 1;
      currentSlideRef.current = next;
      return next;
    });
  };

  const handleNext = () => {
    setCurrentSlide(prev => {
      const next = prev < totalSlides - 1 ? prev + 1 : 0;
      currentSlideRef.current = next;
      return next;
    });
  };

  const handlePlayPause = () => {
    const newPlaying = !isPlayingRef.current;
    isPlayingRef.current = newPlaying;
    setIsPlaying(newPlaying);
    if (newPlaying) {
      startImageProgress();
    } else {
      clearProgressInterval();
    }
  };

  const initializeCarousel = () => {
    isPlayingRef.current = true;
    setIsPlaying(true);
    startImageProgress();
  };

  useEffect(() => {
    if (!carouselData) return undefined;
        
    // Initialize carousel
    initializeCarousel();
    
    // Reset animation states when carousel data changes
    setImageLoaded(false);
    setTitleVisible(false);
    
    // Simulate image loading (you can replace this with actual image loading logic)
    const imageLoadTimer = setTimeout(() => {
      setImageLoaded(true);
    }, 300);
    
    return () => {
      clearProgressInterval();
      clearTimeout(imageLoadTimer);
    };
  }, [carouselData]);

  // Handle title animation after image is loaded
  useEffect(() => {
    if (imageLoaded) {
      const titleTimer = setTimeout(() => {
        setTitleVisible(true);
      }, 200); // Delay after image loads
      
      return () => clearTimeout(titleTimer);
    }
    return undefined;
  }, [imageLoaded]);

  useEffect(() => {
    if (!carouselData) return undefined;
    
    // Reset animation states for new slide
    setImageLoaded(false);
    setTitleVisible(false);
    handleSlideChange();
    
    const imageLoadTimer = setTimeout(() => {
      setImageLoaded(true);
    }, 100);
    
    return () => clearTimeout(imageLoadTimer);
  }, [currentSlide]);

  const formatImageUrl = (fileReference: FileReference | string) => {
    // Handle both old format (object with serverPath/assetPath) and new format (simple string)
    let url = '/images/generic-default-image.jpg';
    if (typeof fileReference === 'string' && fileReference.startsWith('/images/')) {
      url = fileReference;
    } else if (
      fileReference &&
      typeof fileReference === 'object' &&
      fileReference !== null &&
      fileReference !== undefined
    ) {
      const assetName = (fileReference as any).assetName;
      url = assetName.startsWith('/images/') ? assetName : '/images/gehealthcarestage/' + assetName;
    }
    return url.replace(/'/g, '%27').replace(/"/g, '%22');
  };

  const cleanHtmlString = (htmlString: string) => {
    return htmlString.replaceAll(String.raw`\u003C`, '<').replaceAll(String.raw`\u003E`, '>');
  };

  if (!carouselData) {
    return null;
  }

  if (!carouselItem || !carouselItem.fileReference) {
    return null;
  }

  return (
    <div 
      className={`ge-homepage-hero-carousel`}
      ref={carouselContainerRef}
      data-cmp-delay={carouselData.delay}
      data-cmp-slick-autoplay={isAutoplay ? '1' : '0'}
    >
      <div className="cmp-carousel__content">
        <div className="ge-hero-carousel__carousel-container">
          <div 
            className={`ge-outer-media_container ${imageLoaded ? 'image-loaded' : ''}`}
            style={{
              backgroundImage: carouselItem.mediaType === 'image' 
                ? `url("${formatImageUrl(carouselItem.fileReference)}")` 
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          >
            {/* Content overlay - only visible on desktop */}
            <div className="ge-hero-content-overlay">
              {/* Title container - positioned on the left */}
              <div className={`ge-hero-carousel__captions_wrap ${titleVisible ? 'title-visible' : ''}`}>
                {carouselItem.title && (
                  <div className="ge-hero-title-container">
                    {/* Paddles row - above eyebrow/title */}
                    {totalSlides > 1 && (
                      <div className="ge-hero-paddles-in-card">
                        <div className="ge-hero-slide-count text-paragraph-s">
                          <span className="current-slide">{currentSlide + 1}</span>
                          <span className="slide-separator"> / </span>
                          <span className="total-slides">{totalSlides}</span>
                        </div>
                        <div className="ge-hero-paddle-buttons">
                          <button
                            type="button"
                            title="Previous button"
                            className="slick-arrow slick-prev trackable"
                            aria-label="Previous button"
                            onClick={handlePrevious}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.2496 15.0005C11.184 15.0013 11.119 14.9884 11.0587 14.9626C10.9984 14.9367 10.9442 14.8986 10.8996 14.8505L4.39957 8.35054C4.35173 8.30565 4.31361 8.25143 4.28755 8.19124C4.26149 8.13104 4.24805 8.06614 4.24805 8.00054C4.24805 7.93495 4.26149 7.87005 4.28755 7.80985C4.31361 7.74965 4.35173 7.69543 4.39957 7.65054L10.8996 1.15054C10.9445 1.10271 10.9987 1.06459 11.0589 1.03853C11.1191 1.01247 11.184 0.999023 11.2496 0.999023C11.3152 0.999023 11.3801 1.01247 11.4403 1.03853C11.5005 1.06459 11.5547 1.10271 11.5996 1.15054C11.6474 1.19543 11.6855 1.24965 11.7116 1.30985C11.7376 1.37005 11.7511 1.43495 11.7511 1.50054C11.7511 1.56614 11.7376 1.63104 11.7116 1.69124C11.6855 1.75143 11.6474 1.80565 11.5996 1.85054L5.45957 8.00054L11.5996 14.1505C11.6474 14.1954 11.6855 14.2496 11.7116 14.3098C11.7376 14.37 11.7511 14.4349 11.7511 14.5005C11.7511 14.5661 11.7376 14.631 11.7116 14.6912C11.6855 14.7514 11.6474 14.8057 11.5996 14.8505C11.5549 14.8986 11.5007 14.9367 11.4404 14.9626C11.3802 14.9884 11.3151 15.0013 11.2496 15.0005Z" fill="#6022A6"/>
                            </svg>
                          </button>
                          <button
                            type="button"
                            className={`play-pause-button-hc trackable ${isPlaying ? 'active' : ''}`}
                            aria-label="play pause button"
                            onClick={handlePlayPause}
                          >
                            <span className="play">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.2742 7.58306C13.3438 7.62993 13.4008 7.69263 13.4401 7.76576C13.4794 7.83889 13.5 7.92027 13.5 8.00289C13.5 8.0855 13.4794 8.16688 13.4401 8.24002C13.4008 8.31315 13.3438 8.37585 13.2742 8.42271L3.29812 14.92C3.21132 14.9705 3.1126 14.998 3.01163 15C2.92608 14.9992 2.84193 14.9787 2.76606 14.94C2.68603 14.8961 2.61929 14.8322 2.57259 14.755C2.52588 14.6778 2.50085 14.5899 2.50003 14.5002V1.50557C2.49896 1.41435 2.52345 1.32458 2.57087 1.24599C2.61829 1.1674 2.68683 1.10299 2.76905 1.05974C2.85127 1.01649 2.94403 0.996048 3.0373 1.00063C3.13056 1.00521 3.22076 1.03465 3.29812 1.08574L13.2742 7.58306Z" fill="#6022A6"/>
                              </svg>
                            </span>
                            <span className="pause">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M5.5 14.8987H4.5C4.36739 14.8987 4.24021 14.8468 4.14645 14.7543C4.05268 14.6619 4 14.5366 4 14.4059V1.59432C4 1.46363 4.05268 1.3383 4.14645 1.24589C4.24021 1.15348 4.36739 1.10156 4.5 1.10156H5.5C5.63261 1.10156 5.75979 1.15348 5.85355 1.24589C5.94732 1.3383 6 1.46363 6 1.59432V14.4059C6 14.5366 5.94732 14.6619 5.85355 14.7543C5.75979 14.8468 5.63261 14.8987 5.5 14.8987ZM12 14.4059V1.59432C12 1.46363 11.9473 1.3383 11.8536 1.24589C11.7598 1.15348 11.6326 1.10156 11.5 1.10156H10.5C10.3674 1.10156 10.2402 1.15348 10.1464 1.24589C10.0527 1.3383 10 1.46363 10 1.59432V14.4059C10 14.5366 10.0527 14.6619 10.1464 14.7543C10.2402 14.8468 10.3674 14.8987 10.5 14.8987H11.5C11.6326 14.8987 11.7598 14.8468 11.8536 14.7543C11.9473 14.6619 12 14.5366 12 14.4059Z" fill="#6022A6"/>
                              </svg>
                            </span>
                          </button>
                          <div className="progress-circle">
                            <svg className="progress-ring" viewBox="0 0 48 48">
                              <circle className="progress-ring__track" cx="24" cy="24" r="21.5" />
                              <circle
                                className="progress-ring__fill"
                                cx="24"
                                cy="24"
                                r="21.5"
                                style={{ strokeDashoffset: ringCircumference - (progress / 360) * ringCircumference }}
                              />
                            </svg>
                            <button
                              type="button"
                              title="Next button"
                              className="slick-arrow slick-next trackable"
                              aria-label="Next button"
                              onClick={handleNext}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M4.74957 0.999458C4.68399 0.998659 4.61897 1.01159 4.55869 1.03743C4.49842 1.06326 4.44421 1.10142 4.39957 1.14946C4.35173 1.19435 4.31361 1.24857 4.28755 1.30876C4.26149 1.36896 4.24805 1.43386 4.24805 1.49946C4.24805 1.56505 4.26149 1.62996 4.28755 1.69015C4.31361 1.75035 4.35173 1.80457 4.39957 1.84946L10.5396 7.99946L4.39957 14.1495C4.35173 14.1943 4.31361 14.2486 4.28755 14.3088C4.26149 14.369 4.24805 14.4339 4.24805 14.4995C4.24805 14.5651 4.26149 14.63 4.28755 14.6902C4.31361 14.7504 4.35173 14.8046 4.39957 14.8495C4.44446 14.8973 4.49867 14.9354 4.55887 14.9615C4.61907 14.9875 4.68397 15.001 4.74957 15.001C4.81516 15.001 4.88006 14.9875 4.94026 14.9615C5.00046 14.9354 5.05468 14.8973 5.09957 14.8495L11.5996 8.34946C11.6474 8.30457 11.6855 8.25035 11.7116 8.19015C11.7376 8.12995 11.7511 8.06505 11.7511 7.99946C11.7511 7.93386 11.7376 7.86896 11.7116 7.80876C11.6855 7.74857 11.6474 7.69435 11.5996 7.64946L5.09957 1.14946C5.05492 1.10142 5.00072 1.06326 4.94044 1.03743C4.88016 1.01159 4.81514 0.998659 4.74957 0.999458Z" fill="#6022A6"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <h1 className="title-m" dangerouslySetInnerHTML={{ __html: cleanHtmlString(carouselItem.title) }} />

                    {carouselItem.bodyCopyText && (
                      <div className="text-paragraph-s ge-hero-body-text" dangerouslySetInnerHTML={{ __html: cleanHtmlString(carouselItem.bodyCopyText) }} />
                    )}

                    {/* CTA Link */}
                    {carouselItem.text && carouselItem.url && (
                      <div className="ge-hero-carousel__cta">
                        {(() => {
                          let ctaClassName = '';
                          if (carouselItem.icon) {
                            ctaClassName = carouselItem.suffixIcon === 'true' ? 'suffix' : 'prefix';
                          }
                          return (
                            <CTAComponent
                              text={carouselItem.text}
                              href={carouselItem.url}
                              target={carouselItem.target}
                              style={carouselItem.style}
                              svgContent={carouselItem.icon}
                              suffixIcon={carouselItem.suffixIcon === 'true'}
                              accessibilityLabel={carouselItem.text}
                              className={ctaClassName}
                            />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet Content Container - separate from image */}
          <div className="ge-hero-mobile-content" style={{ backgroundColor: '#f4f1f8', minHeight: '200px' }}>
            {/* Title for mobile/tablet */}
            {carouselItem.title && (
              <div className="ge-hero-carousel__captions_wrap">
                <h1 className="title-m" dangerouslySetInnerHTML={{ __html: cleanHtmlString(carouselItem.title) }} />
              </div>
            )}

            {/* Body text for mobile/tablet */}
            {carouselItem.bodyCopyText && (
              <div className="text-paragraph-s ge-hero-body-text" dangerouslySetInnerHTML={{ __html: cleanHtmlString(carouselItem.bodyCopyText) }} />
            )}

            {/* CTA Link for mobile/tablet */}
            {carouselItem.text && carouselItem.url && (
              <div className="ge-hero-carousel__cta">
                {(() => {
                  let ctaClassName = '';
                  if (carouselItem.icon) {
                    ctaClassName = carouselItem.suffixIcon === 'true' ? 'suffix' : 'prefix';
                  }
                  return (
                    <CTAComponent
                      text={carouselItem.text}
                      href={carouselItem.url}
                      target={carouselItem.target}
                      style={carouselItem.style}
                      svgContent={carouselItem.icon}
                      suffixIcon={carouselItem.suffixIcon === 'true'}
                      accessibilityLabel={carouselItem.text}
                      className={ctaClassName}
                    />
                  );
                })()}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default HomepageHeroV2;